import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import {
  ORG_TYPE_LABELS, ORG_STATUS_LABELS, money, shortDate, teamColorStyle
} from './constants';
import { CrmSettingsContext } from './CrmLayout';
import './Organizations.css';

// The account list. Search matches name, short name, mascot, and town -- in the
// field you remember "the Antlers" or "that school in Comfort", not the legal name.

const BLANK_ORG = {
  name: '',
  short_name: '',
  org_type: 'isd',
  status: 'prospect',
  uil_classification: '',
  enrollment: '',
  mascot: '',
  colors: '',
  city: '',
  state: 'TX',
  phone: '',
};

const NewOrgModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState(BLANK_ORG);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError('Name is required');
    try {
      setSaving(true);
      const payload = { ...form, enrollment: form.enrollment === '' ? null : Number(form.enrollment) };
      const response = await api.post('/crm/organizations', payload);
      onCreated(response.data.organization);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create the account');
      setSaving(false);
    }
  };

  return (
    <div className="crm-modal-backdrop" onClick={onClose}>
      <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
        <h2>New account</h2>
        <form onSubmit={submit}>
          {error && <p className="form-error">{error}</p>}

          <label>
            Name
            <input value={form.name} onChange={set('name')} placeholder="Ingram ISD" autoFocus />
          </label>

          <div className="form-row">
            <label>
              Type
              <select value={form.org_type} onChange={set('org_type')}>
                {Object.entries(ORG_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label>
              Status
              <select value={form.status} onChange={set('status')}>
                {Object.entries(ORG_STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          </div>

          <div className="form-row">
            <label>
              Mascot
              <input value={form.mascot} onChange={set('mascot')} placeholder="Warriors" />
            </label>
            <label>
              Colors
              <input value={form.colors} onChange={set('colors')} placeholder="Navy / Gold" />
            </label>
          </div>

          <div className="form-row">
            <label>
              City
              <input value={form.city} onChange={set('city')} placeholder="Ingram" />
            </label>
            <label>
              Classification
              <input value={form.uil_classification} onChange={set('uil_classification')} placeholder="3A" />
            </label>
            <label>
              Enrollment
              <input type="number" value={form.enrollment} onChange={set('enrollment')} />
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Create account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Organizations = () => {
  const navigate = useNavigate();
  const { settings, viewer } = useContext(CrmSettingsContext);
  const [reps, setReps] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [organizations, setOrganizations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(searchParams.get('new') === '1');
  const [filters, setFilters] = useState({ search: '', status: '', orgType: '', ownerUserId: '' });

  const fetchOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.orgType) params.append('orgType', filters.orgType);
      if (filters.ownerUserId) params.append('ownerUserId', filters.ownerUserId);

      const response = await api.get(`/crm/organizations?${params.toString()}`);
      setOrganizations(response.data.organizations);
      setTotal(response.data.total);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const timer = setTimeout(fetchOrganizations, filters.search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [fetchOrganizations, filters.search]);

  useEffect(() => {
    if (!viewer.canSeeAll) return;
    api.get('/crm/reps').then(r => setReps(r.data.reps)).catch(() => {});
  }, [viewer.canSeeAll]);

  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  return (
    <div className="crm-organizations">
      <div className="dashboard-header">
        <div>
          <h1>Accounts</h1>
          <p className="subtitle">
            {viewer.canSeeAll ? 'Schools, leagues, and clubs' : 'Your accounts'}.{' '}
            {total} {total === 1 ? 'account' : 'accounts'}.
          </p>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <input
            value={filters.search}
            onChange={(e) => setFilter('search', e.target.value)}
            placeholder="Search name, mascot, or town…"
          />
        </div>
        <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)}>
          <option value="">All statuses</option>
          {Object.entries(ORG_STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.orgType} onChange={(e) => setFilter('orgType', e.target.value)}>
          <option value="">All types</option>
          {Object.entries(ORG_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {viewer.canSeeAll && reps.length > 0 && (
          <select value={filters.ownerUserId} onChange={(e) => setFilter('ownerUserId', e.target.value)}>
            <option value="">Every rep</option>
            {reps.map(r => (
              <option key={r.id} value={r.id}>
                {[r.first_name, r.last_name].filter(Boolean).join(' ') || r.username}
              </option>
            ))}
          </select>
        )}
      </div>

      {error && <p className="error-banner">{error}</p>}

      {loading && organizations.length === 0 ? (
        <p className="loading">Loading…</p>
      ) : organizations.length === 0 ? (
        <div className="empty-state">
          <h3>No accounts yet</h3>
          <p>
            Your market is a finite list — every district, private school, youth league, and club
            within driving distance. Add them once and they're permanent.
          </p>
          <button type="button" className="btn-primary" onClick={() => setShowNew(true)}>
            Add the first account
          </button>
        </div>
      ) : (
        <ul className="org-cards">
          {organizations.map(org => {
            const colors = settings.use_team_colors ? teamColorStyle(org) : null;
            return (
            <li key={org.id}>
              <button type="button" onClick={() => navigate(`/crm/organizations/${org.id}`)}>
                {colors && <div className="team-stripe" style={colors} aria-hidden="true" />}
                <div className="org-head">
                  <span className="org-name">{org.name}</span>
                  <span className={`status-pill status-${org.status}`}>{ORG_STATUS_LABELS[org.status]}</span>
                </div>
                <div className="org-sub">
                  {[ORG_TYPE_LABELS[org.org_type], org.uil_classification, org.city, org.mascot]
                    .filter(Boolean).join(' · ')}
                </div>
                <div className="org-stats">
                  <span>{org.program_count} {Number(org.program_count) === 1 ? 'program' : 'programs'}</span>
                  <span>{org.contact_count} {Number(org.contact_count) === 1 ? 'contact' : 'contacts'}</span>
                  <span className="org-value">{money(org.lifetime_value)} lifetime</span>
                  <span className="org-last">
                    {org.last_activity_at ? `Last touch ${shortDate(org.last_activity_at)}` : 'Never contacted'}
                  </span>
                  {viewer.canSeeAll && (
                    <span className="org-owner-tag">
                      {org.owner_first_name
                        ? [org.owner_first_name, org.owner_last_name].filter(Boolean).join(' ')
                        : 'Unassigned'}
                    </span>
                  )}
                </div>
              </button>
            </li>
            );
          })}
        </ul>
      )}

      {showNew && (
        <NewOrgModal
          onClose={() => { setShowNew(false); if (searchParams.get('new')) setSearchParams({}, { replace: true }); }}
          onCreated={(org) => navigate(`/crm/organizations/${org.id}`)}
        />
      )}
    </div>
  );
};

export default Organizations;
